/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ICrossLocationOpenShiftDataService, ICrossLocationShiftDataService, ICrossLocationTeamDataService, IUserProfileDataService, ITeamSettingsDataService } from "@microsoft/shifts-managers";
import {
    ECSConfigKey,
    ECSConfigService,
    IAllDataServices,
    IConflictDismissDataService,
    IImportJobsDataService,
    IJobDataService,
    IMemberDataService,
    INoteDataService,
    IScheduleFiltersDataService,
    IShiftDataService,
    IShiftRequestDataService,
    ITagDataService,
    ITeamDataService,
    ITeamMemberPermissionsDataService,
    ITeamPermissionsDataService,
    ITeamSettingDataService,
    ITenantDataService,
    ITimeClockDataService,
    ITimeOffReasonDataService,
    IUserDataService,
    ISmartScheduleDataService,
    IUserTeamSettingsDataService
} from "sh-services";
import AppDb from "sh-services/data/AppDb";

import IDataService from "./IDataService";
import { InstrumentationService } from "../lib/InstrumentationService/InstrumentationService";

/**
 * This class provides a mechanism for all DataServices to be notified about login events
 * TODO: This needs to be replaced with a pubsub system in base data service,
 *       where dataservice register themselves to be notified about these events
 */
export class AllDataServices implements IAllDataServices {
    jobDataService: IJobDataService;
    memberDataService: IMemberDataService;
    noteDataService: INoteDataService;
    scheduleFiltersDataService: IScheduleFiltersDataService;
    shiftDataService: IShiftDataService;
    shiftRequestDataService: IShiftRequestDataService;
    tagDataService: ITagDataService;
    teamSettingDataService: ITeamSettingDataService;
    timeOffReasonDataService: ITimeOffReasonDataService;
    teamDataService: ITeamDataService;
    tenantDataService: ITenantDataService;
    userDataService: IUserDataService;
    timeClockDataService: ITimeClockDataService;
    conflictDismissDataService: IConflictDismissDataService;
    teamPermissionsDataService: ITeamPermissionsDataService;
    teamMemberPermissionsDataService: ITeamMemberPermissionsDataService;
    importJobsDataService: IImportJobsDataService;
    crossLocationOpenShiftDataService: ICrossLocationOpenShiftDataService;
    crossLocationTeamDataService: ICrossLocationTeamDataService;
    userProfileDataService: IUserProfileDataService;
    crossLocationShiftDataService: ICrossLocationShiftDataService;
    teamSettingsDataService: ITeamSettingsDataService;
    smartScheduleDataService: ISmartScheduleDataService;
    userTeamSettingsDataService: IUserTeamSettingsDataService;

    constructor( jobDataService: IJobDataService,
        memberDataService: IMemberDataService,
        noteDataService: INoteDataService,
        scheduleFiltersDataService: IScheduleFiltersDataService,
        shiftDataService: IShiftDataService,
        shiftRequestDataService: IShiftRequestDataService,
        tagDataService: ITagDataService,
        teamSettingDataService: ITeamSettingDataService,
        timeOffReasonDataService: ITimeOffReasonDataService,
        teamDataService: ITeamDataService,
        tenantDataService: ITenantDataService,
        userDataService: IUserDataService,
        timeClockDataService: ITimeClockDataService,
        conflictDismissDataService: IConflictDismissDataService,
        teamPermissionsDataService: ITeamPermissionsDataService,
        teamMemberPermissionsDataService: ITeamMemberPermissionsDataService,
        importJobsDataService: IImportJobsDataService,
        crossLocationOpenShiftDataService: ICrossLocationOpenShiftDataService,
        crossLocationTeamDataService: ICrossLocationTeamDataService,
        userProfileDataService: IUserProfileDataService,
        crossLocationShiftDataService: ICrossLocationShiftDataService,
    teamSettingsDataService: ITeamSettingsDataService,
    smartScheduleDataService: ISmartScheduleDataService,
    userTeamSettingsDataService: IUserTeamSettingsDataService
        ) {
        this.jobDataService = jobDataService;
        this.memberDataService = memberDataService;
        this.noteDataService = noteDataService;
        this.scheduleFiltersDataService = scheduleFiltersDataService;
        this.shiftDataService = shiftDataService;
        this.shiftRequestDataService = shiftRequestDataService;
        this.tagDataService = tagDataService;
        this.teamSettingDataService = teamSettingDataService;
        this.timeOffReasonDataService = timeOffReasonDataService;
        this.teamDataService = teamDataService;
        this.tenantDataService = tenantDataService;
        this.userDataService = userDataService;
        this.timeClockDataService = timeClockDataService;
        this.conflictDismissDataService = conflictDismissDataService;
        this.teamPermissionsDataService = teamPermissionsDataService;
        this.teamMemberPermissionsDataService = teamMemberPermissionsDataService;
        this.importJobsDataService = importJobsDataService;
        this.crossLocationOpenShiftDataService = crossLocationOpenShiftDataService;
        this.crossLocationTeamDataService = crossLocationTeamDataService;
        this.userProfileDataService = userProfileDataService;
        this.crossLocationShiftDataService = crossLocationShiftDataService;
        this.teamSettingsDataService = teamSettingsDataService;
        this.smartScheduleDataService = smartScheduleDataService;
        this.userTeamSettingsDataService = userTeamSettingsDataService;
    }

    /**
     * Gets a list of all dataservices.
     */
    private get DataServices(): IDataService[] {
        return [
            this.jobDataService,
            this.memberDataService,
            this.noteDataService,
            this.scheduleFiltersDataService,
            this.shiftDataService,
            this.shiftRequestDataService,
            this.tagDataService,
            this.teamSettingDataService,
            this.timeOffReasonDataService,
            this.teamDataService,
            this.tenantDataService,
            this.userDataService,
            this.timeClockDataService,
            this.conflictDismissDataService,
            this.teamPermissionsDataService,
            this.teamMemberPermissionsDataService,
            this.importJobsDataService,
            this.crossLocationOpenShiftDataService,
            this.crossLocationTeamDataService,
            this.userProfileDataService,
            this.crossLocationShiftDataService,
            this.teamSettingsDataService,
            this.smartScheduleDataService,
            this.userTeamSettingsDataService
        ];
    }

    /**
     * Sets the user as logged in all data services and add given info in UserStore.
     * @param loggedInUserId The logged in user id.
     * @param tenantId The logged in user tenant id.
     * @param sessionId The client session id.
     */
    public async setUserLoggedIn(loggedInUserId: string, tenantId: string, sessionId: string): Promise<void> {
        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.ParellelizeIndexedDbInitialization)) {
            await Promise.allSettled(this.DataServices.map(dataService => dataService.setUserLoggedIn(loggedInUserId, tenantId, sessionId)));
        } else {
            for (const dataService of this.DataServices) {
                await dataService.setUserLoggedIn(loggedInUserId, tenantId, sessionId);
            }
        }
    }

    /**
     * Prune any non-essential data
     */
    public async pruneData() {

        const oldSessionIdsToDelete = await this.userDataService.getOldSessionIdsToDelete();

        // currently only data service that store session data need to be pruned to delete old sessions
        await this.userDataService.deleteSessionData(oldSessionIdsToDelete);
        await this.teamDataService.deleteSessionData(oldSessionIdsToDelete);
        await this.noteDataService.deleteSessionData(oldSessionIdsToDelete);
        await this.shiftDataService.deleteSessionData(oldSessionIdsToDelete);
    }

    /**
     * Resets the sync state across all data services
     */
    public async resetSyncState() {
        for (const dataService of this.DataServices) {
            await dataService.resetSyncState();
        }
    }

    /**
     * Set the user as logged out in all data services
     */
    public async setUserLoggedOut() {
        for (const dataService of this.DataServices) {
            await dataService.setUserLoggedOut();
        }
    }

    /**
     * Calls all locally stored data in the app
     */
    public async resetAppData() {
        // clear all databases
        await AppDb.deleteAllDatabases();

        // Preserve landing-page telemetry state so the post-reload
        // ScheduleSeen event can still compute TimeSinceLandingPageMs and LandingPageMode.
        let landingPageReachedAt: string | null = null;
        let landingPageMode: string | null = null;
        try {
            landingPageReachedAt = sessionStorage.getItem(InstrumentationService.LANDING_PAGE_REACHED_KEY);
            landingPageMode = sessionStorage.getItem(InstrumentationService.LANDING_PAGE_MODE_KEY);
        } catch (_) {}

        // clear local storage
        localStorage.clear();
        // clear session storage
        sessionStorage.clear();

        try {
            if (landingPageReachedAt !== null) {
                sessionStorage.setItem(InstrumentationService.LANDING_PAGE_REACHED_KEY, landingPageReachedAt);
            }
            if (landingPageMode !== null) {
                sessionStorage.setItem(InstrumentationService.LANDING_PAGE_MODE_KEY, landingPageMode);
            }
        } catch (_) {}
    }

    /**
     * Job Data Service
     */
    public get JobDataService(): IJobDataService {
        return this.jobDataService;
    }

    /**
     * Member Data Service
     */
    public get MemberDataService(): IMemberDataService {
        return this.memberDataService;
    }

    /**
     * Note Data Service
     */
    public get NoteDataService(): INoteDataService {
        return this.noteDataService;
    }

    /**
     * Shift Data Service
     */
    public get ShiftDataService(): IShiftDataService {
        return this.shiftDataService;
    }

    /**
     * ShiftRequest Data Service
     */
    public get ShiftRequestDataService(): IShiftRequestDataService {
        return this.shiftRequestDataService;
    }

    /**
     * Tag Data Service
     */
    public get TagDataService(): ITagDataService {
        return this.tagDataService;
    }

    /**
     * Team Setting Data Service
     */
    public get TeamSettingDataService(): ITeamSettingDataService {
        return this.teamSettingDataService;
    }

    /**
     * Time Off Reasons Data Service
     */
    public get TimeOffReasonDataService(): ITimeOffReasonDataService {
        return this.timeOffReasonDataService;
    }

    /**
     * Team Data Service
     */
    public get TeamDataService(): ITeamDataService {
        return this.teamDataService;
    }

    /**
     * Tenant Data Service
     */
    public get TenantDataService(): ITenantDataService {
        return this.tenantDataService;
    }

    /**
     * User Data Service
     */
    public get UserDataService(): IUserDataService {
        return this.userDataService;
    }

    /**
     * Conflict Dismiss Data Service
     */
    public get ConflictDismissDataService(): IConflictDismissDataService {
        return this.conflictDismissDataService;
    }

    /**
     * Team permissions Data Service
     */
    public get TeamPermissionsDataService(): ITeamPermissionsDataService {
        return this.teamPermissionsDataService;
    }

    /**
     * Team member permissions Data Service
     */
    public get TeamMemberPermissionsDataService(): ITeamMemberPermissionsDataService {
        return this.teamMemberPermissionsDataService;
    }

    /**
     * Import Jobs Data Service
     */
    public get ImportJobsDataService(): IImportJobsDataService {
        return this.importJobsDataService;
    }

    /**
     * The cross-location open shift data service.
     * @returns The cross-location open shift data service.
     */
    public get CrossLocationOpenShiftDataService(): ICrossLocationOpenShiftDataService {
        return this.crossLocationOpenShiftDataService;
    }

    /**
     * The cross-location shift data service.
     * @returns The cross-location shift data service.
     */
    public get CrossLocationShiftDataService(): ICrossLocationShiftDataService {
        return this.crossLocationShiftDataService;
    }

    /**
     * The cross-location team data service.
     * @returns The cross-location team data service.
     */
    public get CrossLocationTeamDataService(): ICrossLocationTeamDataService {
        return this.crossLocationTeamDataService;
    }

    /**
     * The user profile data service.
     * @returns The user profile data service.
     */
    public get UserProfileDataService(): IUserProfileDataService {
        return this.userProfileDataService;
    }

    /**
     * The team settings data service.
     * @returns The team settings data service.
     */
    public get TeamSettingsDataService(): ITeamSettingsDataService {
        return this.teamSettingsDataService;
    }

    /**
     * Smart Schedule Data Service
     */
    public get SmartScheduleDataService(): ISmartScheduleDataService {
        return this.smartScheduleDataService;
    }

    /**
     * User Team Settings Data Service
     * Manages user-specific configuration per team (e.g., mutedScheduleGroupIds)
     */
    public get UserTeamSettingsDataService(): IUserTeamSettingsDataService {
        return this.userTeamSettingsDataService;
    }
}